/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getEnumAll } from "components/axios/commonAxios";
import { getEquipmentCategoriesByQuery } from "components/axios/equipmentCategoryAxios";
import { getUsersByQuery } from "components/axios/userAxios";
import { getEquipmentZoneCategories, getEquipmentZoneCategoriesByManagementCompanyId } from "components/axios/equipmentZoneAxios";
import { EnumApiContext } from "contexts/EnumApiContext";

function EnumValuesStorage({ children }) {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { loginId } = useSelector(state => state.user);
  
  const [loginInfo, setLoginInfo] = useState({ role: 'NONE' });
  const [role, setRole] = useState('NONE');

  const lang = i18n.language.substring(0, 2);
  const isLoggedIn = role !== 'NONE' && loginId !== 'NONE';

  // 1. 언어 변경 시 쿼리 무효화 관리
  useEffect(() => {
    const invalidate = () => queryClient.invalidateQueries();
    i18n.on('languageChanged', invalidate);
    return () => i18n.off('languageChanged', invalidate);
  }, [queryClient, i18n]);

  // 2. 사용자 정보 조회 (useQuery 활용)
  const {
    data: userData,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useQuery(
    ['userByLoginId', loginId],
    () => getUsersByQuery({ loginId }).then(data => data?._embedded?.users?.[0]),
    {
      enabled: !!loginId && loginId !== 'NONE',
      onSuccess: (data) => {
        // console.log(data)
        if (data) {
          setLoginInfo({
            id: data.id,
            loginId: data.loginId,
            role: data.role,
            company: { 
              id: data.company?.id, 
              type: data.company?.type, 
              name: data.company?.info?.name 
            }
          });
          setRole(data.role);
        } else {
          setLoginInfo({ role: 'NONE' });
          setRole('NONE');
        }
      }
    }
  );

  // 3. Enum 값 조회
  const { data: enumValues = {} } = useQuery(
    ['enumValues', i18n.language], 
    getEnumAll
  );

  // 4. 장비 카테고리 조회
  const { data: equipmentCategoryLevel1s = [] } = useQuery(
    ['equipmentCategoryLevel1s', loginId],
    () => getEquipmentCategoriesByQuery({ depth: 1, size: 100, sort: "name,asc" }),
    {
      enabled: isLoggedIn,
      staleTime: 1000 * 60 * 5,
      select: (data) => data?._embedded?.equipmentCategories || []
    }
  );

  // 5. 지하철 노선 데이터 가공  getEquipmentZoneCategoriesByManagementCompanyId
  const useSubwayLines = (path) => {
    // console.log("isLoggedIn:", isLoggedIn);
    const query = useQuery(
      ['subwayLines', loginId, lang, path],
      () => getEquipmentZoneCategoriesByManagementCompanyId(path),
      {
        enabled: isLoggedIn && !!path /*&& (process.env.REACT_APP_PLATFORM_SERVER === 'NANTONG')*/,
        // placeholderData: [], // ⭐ 중요
        placeholderData: previousData => previousData, // ⭐ 핵심
        // staleTime: 1000 * 60 * 5,
        select: (data) =>
          Object.fromEntries(
            data.map(line => [
              line.code,
              {
                ...line,
                title: line.name?.[lang] || line.name?.ko
              }
            ])
          )
      }
    );

    const map = query.data || {};

    return {
      ...query,
      subwayLineMap: map,
      subwayLines: Object.values(map),
      subwayLineCount: Object.keys(map).length,
      subwayLineByCode: (code) => map[code]
    };
  };

  const useNantongSubwayLines = () => {
    const companyId = process.env.REACT_APP_NANTONG_MANAGEMENT_COMPANY_NO;
    return useSubwayLines(companyId);
  };
  // const { data: subwayLineMap = {} } = useQuery(
  //   ['subwayLines', loginId, lang],
  //   getEquipmentZoneCategories,
  //   {
  //     enabled: isLoggedIn && (process.env.REACT_APP_PLATFORM_SERVER === 'NANTONG'),
  //     // enabled: isLoggedIn,
  //     select: (data) => Object.fromEntries(
  //       data.map(line => [
  //         line.code,
  //         { ...line, title: line.name?.[lang] || line.name?.ko }
  //       ])
  //     )
  //   }
  // );

  // 도우미 함수들
  const getEnumValueByEnumName = (enumName) => enumValues?.[enumName] || null;
  const getEnumValueByEnumNameAndCode = (enumName, code) => {
    return getEnumValueByEnumName(enumName)?.filter(d => d.code === code) || null;
  };

  // const subwayLines = useMemo(() => Object.values(subwayLineMap), [subwayLineMap]);
  // const subwayLineCount = subwayLines.length; // 0으로 처리됨
  // const subwayLineByCode = (code) => subwayLineMap[code] || null; // undefined 방지

  return (
    <EnumApiContext.Provider value={{ 
        enumValues, getEnumValueByEnumName, getEnumValueByEnumNameAndCode, 
        useSubwayLines, useNantongSubwayLines,
        // subwayLines, subwayLineCount, subwayLineByCode,
        equipmentCategoryLevel1s, role, loginInfo, isUserLoading
      }}>
      {children}
    </EnumApiContext.Provider>
  );
}

export default EnumValuesStorage;